import { useEffect, useRef, useState, type FormEvent } from "react";
import type { AccountResponse } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { ROLE_LABEL } from "../../shared/permissions";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { CardSkeleton } from "@/components/Loading";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changePassword, errorText, fieldErrors, getAccount } from "@/lib/client";
import { MIN_LENGTH, checkNewPasswordFields } from "@/lib/password";
import { formatDay } from "@/lib/upload";

const FIELDS = [
  { name: "currentPassword", label: "Mevcut parola", auto: "current-password" },
  { name: "newPassword", label: "Yeni parola", auto: "new-password" },
  { name: "newPasswordConfirm", label: "Yeni parola (tekrar)", auto: "new-password" },
] as const;

/** Hesabım (ui.md §4.5, ADR-0017): hesap bilgisi + kalıcı parola değiştirme. Bütün roller. */
export function AccountScreen() {
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    let live = true;
    getAccount().then((a) => live && setAccount(a), (e) => live && setLoadError(errorText(e)));
    return () => { live = false; };
  }, []);

  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader title="Hesabım" description="Hesap bilgileriniz ve parolanız." />
      {account ? <Info a={account} /> : loadError ? <ErrorAlert>{loadError}</ErrorAlert> : <CardSkeleton />}
      <PasswordForm />
    </main>
  );
}

function Info({ a }: { a: AccountResponse }) {
  const rows: [string, string, string?][] = [
    ["Kullanıcı adı", a.username], ["Rol", ROLE_LABEL[a.role]], ["Hesap açılışı", formatDay(a.createdAt)],
    ["Önceki giriş", a.previousLoginAt ? formatAuditTime(a.previousLoginAt) : "Bu hesabın ilk girişi.", a.previousLoginAt ? "tabular-nums" : ""],
  ];
  return (
    <Card role="region" aria-labelledby="account-h" className="max-w-xl gap-3 text-sm">
      <div className="flex items-center gap-3">
        <UserAvatar name={a.username} size="md" />
        <div className="flex flex-col"><span className="font-medium">{a.username}</span><span className="text-muted-foreground">{ROLE_LABEL[a.role]}</span></div>
      </div>
      <h2 id="account-h" className="sr-only">Hesap bilgisi</h2>
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {rows.map(([k, v, cls]) => <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className={cls}>{v}</dd></div>)}
      </dl>
    </Card>
  );
}

function PasswordForm() {
  const form = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);
  useEffect(() => { if (done) status.current?.focus(); }, [done]); // the submit button was disabled and dropped focus

  const fail = (e: Record<string, string>) => {
    setErrors(e);
    const first = FIELDS.find((f) => e[f.name]);
    if (first) form.current?.querySelector<HTMLInputElement>(`[name=${first.name}]`)?.focus();
  };

  async function submit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const f = new FormData(ev.currentTarget);
    const currentPassword = String(f.get("currentPassword")), newPassword = String(f.get("newPassword")), newPasswordConfirm = String(f.get("newPasswordConfirm"));
    setDone(false); setFormError("");
    const local = checkNewPasswordFields(newPassword, newPasswordConfirm);
    if (local) return fail({ [local.field]: local.message });
    setErrors({}); setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword, newPasswordConfirm });
      form.current?.reset();
      setDone(true);
    } catch (e) {
      const fe = fieldErrors(e);
      if (fe) fail(fe); else setFormError(errorText(e));
    } finally { setBusy(false); }
  }

  return (
    <Card role="region" aria-labelledby="pw-h" className="max-w-xl gap-3">
      <h2 id="pw-h" className="text-lg font-semibold">Parola değiştir</h2>
      <p className="text-sm text-muted-foreground">En az {MIN_LENGTH} karakter, mevcut paroladan farklı. Parola değişince diğer oturumlarınız kapanır.</p>
      {done && <p ref={status} tabIndex={-1} role="status" className="text-sm text-success-foreground outline-none">✓ Parola değiştirildi. Diğer oturumlarınız kapandı.</p>}
      {formError && <ErrorAlert>{formError}</ErrorAlert>}
      <form ref={form} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {FIELDS.map((f) => (
          <div key={f.name} className="flex flex-col gap-2">
            <Label htmlFor={f.name}>{f.label}</Label>
            <Input
              id={f.name} name={f.name} type="password" autoComplete={f.auto} required
              aria-invalid={errors[f.name] ? true : undefined} aria-describedby={errors[f.name] ? `${f.name}-err` : undefined}
            />
            {errors[f.name] && <p id={`${f.name}-err`} className="text-sm text-destructive">{errors[f.name]}</p>}
          </div>
        ))}
        <div className="flex justify-end"><Button type="submit" disabled={busy}>{busy ? "Değiştiriliyor…" : "Parolayı değiştir"}</Button></div>
      </form>
    </Card>
  );
}
